import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  type ReactNode,
} from "react";
import classNames from "classnames";
import Button from "./button";

const Scroll: React.FC<{
  children: ReactNode;
  className?: string;
  bidirectional?: boolean;
  vertical?: boolean;
  itemContainerSelector?: string;
  noButton?: boolean;
}> = ({
  children,
  className,
  vertical: verticalProp,
  bidirectional,
  itemContainerSelector,
  noButton,
}) => {
  const [{ showLB, showRB, showTB, showBB }, setShowButtons] = useState({
    showHB: false,
    showLB: false,
    showRB: false,
    showVB: false,
    showTB: false,
    showBB: false,
  });
  const [element, setElement] = useState<HTMLElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const vertical = bidirectional || verticalProp;
  const horizontal = bidirectional || !verticalProp;
  useEffect(() => {
    let pos = { x: 0, y: 0, left: 0, top: 0 };
    const mouseDownHandler = function (e: MouseEvent) {
      pos = {
        left: element!.scrollLeft,
        top: element!.scrollTop,
        x: e.clientX,
        y: e.clientY,
      };
      element!.addEventListener("mousemove", mouseMoveHandler);
      document!.addEventListener("mouseup", mouseUpHandler);
    };
    element?.addEventListener("mousedown", mouseDownHandler);

    const mouseMoveHandler = function (e: any) {
      if (
        (vertical && element!.offsetHeight < element!.scrollHeight) ||
        (horizontal && element!.offsetWidth < element!.scrollWidth)
      ) {
        setIsDragging(true);
        element!.addEventListener("mousemove", mouseMoveHandler);
        document.addEventListener("mouseup", mouseUpHandler);
      }
      // How far the mouse has been moved
      if (vertical) {
        const dy = e.clientY - pos.y;
        element!.scrollTop = pos.top - dy;
      }
      if (horizontal) {
        const dx = e.clientX - pos.x;
        // Scroll the element
        element!.scrollLeft = pos.left - dx;
      }
    };

    const mouseUpHandler = () => {
      setIsDragging(false);
      element!.removeEventListener("mousemove", mouseMoveHandler);
      document!.removeEventListener("mouseup", mouseUpHandler);
    };

    return () => {
      element?.removeEventListener("mousedown", mouseDownHandler);
    };
  }, [element, vertical, horizontal]);

  const scrollHandler = (
    right: boolean,
    direction: "horizontal" | "vertical"
  ) => {
    //NOSONAR
    const elementChildren = itemContainerSelector
      ? element?.querySelector(itemContainerSelector)?.children
      : element?.children;
    let scrollSize = 0;
    if (elementChildren?.length) {
      let i = 0;
      let item = elementChildren.item(i) as HTMLElement;
      if (direction === "vertical") {
        scrollSize = item.scrollHeight;
        while (++i < elementChildren.length) {
          item = elementChildren.item(i) as HTMLElement;
          if (item.offsetTop >= element!.scrollTop) {
            if (right) {
              scrollSize = item.scrollHeight;
            }
            break;
          }
          scrollSize = item.scrollHeight;
        }
        element?.scrollBy(0, (right ? 1 : -1) * scrollSize);
      } else {
        scrollSize = item.scrollWidth;
        while (++i < elementChildren.length) {
          item = elementChildren.item(i) as HTMLElement;
          if (item.offsetLeft >= element!.scrollLeft) {
            if (right) {
              scrollSize = item.scrollWidth;
            }
            break;
          }
          scrollSize = item.scrollWidth;
        }
        element?.scrollBy((right ? 1 : -1) * scrollSize, 0);
      }
    }
  };

  const manageButtonsVisibility = useCallback(() => {
    if (noButton) {
      return;
    }
    const showH = !!element && element.offsetWidth < element.scrollWidth;
    const showL = !!element && !!element.scrollLeft;
    const showR =
      !!element &&
      element.scrollLeft + element.offsetWidth < element.scrollWidth;
    const showV = !!element && element?.offsetHeight < element?.scrollHeight;
    const showT = !!element && !!element.scrollTop;
    const showB =
      !!element &&
      element.scrollTop + element.offsetHeight < element.scrollHeight;

    setShowButtons((prev) =>
      prev.showHB !== showH ||
      prev.showLB !== showL ||
      prev.showRB !== showR ||
      prev.showHB !== showV ||
      prev.showTB !== showT ||
      prev.showBB !== showB
        ? {
            ...prev,
            showHB: showH,
            showLB: showL,
            showRB: showR,
            showVB: showV,
            showTB: showT,
            showBB: showB,
          }
        : prev
    );
  }, [element, noButton]);

  useEffect(() => {
    if (ref.current) {
      const container: HTMLDivElement = ref.current;
      setElement(
        container.children.length > 0
          ? (container.children[0] as HTMLElement)
          : null
      );
    } else {
      setElement(null);
    }
  }, [ref]);

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    const onScrollOrResize = () => {
      if (timeout) {
        clearTimeout(timeout);
      }
      timeout = setTimeout(manageButtonsVisibility, 50);
    };

    onScrollOrResize();

    window.addEventListener("resize", onScrollOrResize);
    element?.addEventListener("scroll", onScrollOrResize);

    return () => {
      clearTimeout(timeout);
      window.removeEventListener("resize", onScrollOrResize);
      element?.removeEventListener("scroll", onScrollOrResize);
    };
  }, [element, manageButtonsVisibility]);

  useEffect(() => {
    manageButtonsVisibility();
  }, [children, manageButtonsVisibility]);

  return (
    <div
      className={classNames("snapcont", {
        vertical,
        horizontal,
        left: showLB,
        right: showRB,
      })}
    >
      <div
        ref={ref}
        className={classNames("snapscroll", className, {
          dragging: isDragging,
          noButton,
        })}
      >
        {children}
      </div>
      {!noButton && horizontal && (
        <>
          <Button
            data-contain="visible"
            micro
            primary
            className={classNames("slider", "horizontal", "prev", "left", {
              show: showLB,
            })}
            icon="chevron_left"
            onClick={() => scrollHandler(false, "horizontal")}
          />
          <Button
            data-contain="visible"
            micro
            primary
            className={classNames("slider", "horizontal", "next", "right", {
              show: showRB,
            })}
            icon="chevron_right"
            onClick={() => scrollHandler(true, "horizontal")}
          />
        </>
      )}
      {!noButton && vertical && (
        <>
          <Button
            data-contain="visible"
            micro
            primary
            className={classNames("slider", "vertical", "prev", "top", {
              show: showTB,
            })}
            icon="expand_less"
            onClick={() => scrollHandler(false, "vertical")}
          />
          <Button
            data-contain="visible"
            micro
            primary
            className={classNames("slider", "vertical", "next", "bottom", {
              show: showBB,
            })}
            icon="expand_more"
            onClick={() => scrollHandler(true, "vertical")}
          />
        </>
      )}
    </div>
  );
};

export default Scroll;
